import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, CircleNotch, Bank, User as UserIcon, Buildings } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  createBankAccount,
  updateBankAccount,
  type BankAccount,
  type BankAccountOwnerType,
  type BankAccountType,
} from '@/lib/bankAccountsApi';
import { fetchBarristers } from '@/lib/barristersApi';
import { getApiErrorMessage, validationFieldErrors } from '@/lib/api';
import { useAuthStore } from '@/features/auth/store';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

/** The reason a box was refused, directly under that box. */
function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs text-danger">{message}</p>;
}

interface Props {
  initial?: BankAccount;
  /// When opened from a "Mine" section, force the new account to belong to
  /// the caller. The owner picker hides itself in this mode.
  forceOwnerType?: BankAccountOwnerType;
  forceBarristerOwnerId?: string | null;
  onClose: () => void;
  onSaved: (a: BankAccount) => void;
}

const TYPE_OPTIONS: { value: BankAccountType; label: string; help: string }[] = [
  {
    value: 'operating',
    label: 'Operating',
    help: 'Chambers’ own account — fees received, expenses paid.',
  },
  {
    value: 'trust',
    label: 'Trust / Client',
    help: 'Money held on behalf of clients. Kept strictly separate from operating funds (SRA rule).',
  },
  {
    value: 'other',
    label: 'Other',
    help: 'Savings, deposit, foreign-currency, or any account not covered above.',
  },
];

export function BankAccountModal({
  initial,
  forceOwnerType,
  forceBarristerOwnerId,
  onClose,
  onSaved,
}: Props) {
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const isEdit = Boolean(initial);
  const isAdmin = currentUser?.memberType === 'admin';

  // Owner state. Locked when editing (cannot change owner of an existing
  // account — would corrupt the audit trail) or when forced by the parent
  // (e.g. opened from a "My accounts" section).
  const [ownerType, setOwnerType] = useState<BankAccountOwnerType>(
    initial?.ownerType ?? forceOwnerType ?? (currentUser?.barristerId ? 'barrister' : 'chambers'),
  );
  const [barristerOwnerId, setBarristerOwnerId] = useState<string | null>(
    initial?.barristerOwnerId ??
      forceBarristerOwnerId ??
      currentUser?.barristerId ??
      null,
  );

  const ownerLocked = isEdit || Boolean(forceOwnerType);

  // Only admins editing a chambers/admin-managed flow need the barrister
  // dropdown. A barrister creating their own account can't pick someone
  // else, so we skip the fetch when locked.
  const barristersQ = useQuery({
    queryKey: ['barristers', 'all-for-bank-owner-picker'],
    queryFn: fetchBarristers,
    enabled: !ownerLocked && ownerType === 'barrister' && isAdmin,
  });

  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<BankAccountType>(initial?.type ?? 'operating');
  const [bankName, setBankName] = useState(initial?.bankName ?? '');
  const [last4] = useState(initial?.accountNumberLast4 ?? '');
  const [sortCode, setSortCode] = useState(
    initial?.sortCode
      ? `${initial.sortCode.slice(0, 2)}-${initial.sortCode.slice(2, 4)}-${initial.sortCode.slice(4, 6)}`
      : '',
  );
  const [currency, setCurrency] = useState(initial?.currency ?? 'GBP');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const [last4Source, setLast4Source] = useState(''); // user types full account number; we trim

  const computedLast4 = (() => {
    // If the user has typed something into the "full account number" field,
    // take the last 4 numeric characters. Otherwise show what's already saved.
    if (last4Source) {
      const digits = last4Source.replace(/\D/g, '');
      return digits.slice(-4);
    }
    return last4;
  })();

  /**
   * Which boxes were wrong on the last attempt, so the form can mark them.
   *
   * This form used to refuse with a toast alone — "Name is required" floating above an
   * unmarked form — and when the server refused instead, the toast read "Validation failed"
   * and named nothing at all. On the screen that decides where a barrister's fees are paid,
   * that is not good enough: say which box, next to the box.
   */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const clearField = (key: string) =>
    setFieldErrors((f) => (f[key] ? Object.fromEntries(Object.entries(f).filter(([k]) => k !== key)) : f));

  /** Everything the form can know before asking the server. */
  const localErrors = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Give the account a label so it can be told apart.';
    if (ownerType === 'barrister' && !barristerOwnerId) {
      e.barristerOwnerId = 'Pick which barrister this account belongs to.';
    }
    // The sort-code box strips non-digits as you type, so the only way to be wrong is to be
    // incomplete — which the server rejects, and silently losing a half-typed code is worse.
    const sortDigits = sortCode.replace(/\D/g, '');
    if (sortDigits.length > 0 && sortDigits.length < 6) {
      e.sortCode = 'A sort code is 6 digits, e.g. 12-34-56.';
    }
    if (last4Source.trim() && last4Source.replace(/\D/g, '').length < 4) {
      e.accountNumber = 'Enter the account number in digits — at least the last 4.';
    }
    if (!/^[A-Za-z]{3}$/.test(currency.trim())) {
      e.currency = 'Currency is a 3-letter code, e.g. GBP.';
    }
    return e;
  };

  const saveMut = useMutation({
    mutationFn: async (): Promise<BankAccount> => {
      const trimmedName = name.trim();

      const payload = {
        name: trimmedName,
        bankName: bankName.trim() || null,
        accountNumberLast4: computedLast4 || null,
        sortCode: sortCode.replace(/-/g, '').trim() || null,
        currency: currency.toUpperCase().trim() || 'GBP',
        description: description.trim() || null,
        isDefault,
      };

      if (isEdit && initial) {
        return updateBankAccount(initial.id, payload);
      }
      return createBankAccount({
        ...payload,
        type,
        ownerType,
        barristerOwnerId: ownerType === 'barrister' ? barristerOwnerId : null,
      });
    },
    onSuccess: (a) => {
      toast.success(isEdit ? 'Account updated' : 'Account added');
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      onSaved(a);
    },
    onError: (err) => {
      // The server answers a Zod refusal with a per-field map; put each message against its own
      // box rather than only flashing the first one past in a toast.
      const byField = validationFieldErrors(err);
      const mapped: Record<string, string> = {};
      for (const [field, msg] of Object.entries(byField)) {
        mapped[field === 'accountNumberLast4' ? 'accountNumber' : field] = msg;
      }
      if (Object.keys(mapped).length) setFieldErrors(mapped);
      toast.error(getApiErrorMessage(err, 'Could not save the account'));
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saveMut.isPending) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <Bank size={15} weight="duotone" className="text-brand-600 dark:text-brand-400" />
              {isEdit ? 'Edit bank account' : 'Add bank account'}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              We never store the full account number — only the last 4 digits.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !saveMut.isPending && onClose()}
            disabled={saveMut.isPending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {/* Owner — only the picker shows; locked when editing or forced */}
          {!ownerLocked && isAdmin && (
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Belongs to
              </label>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => setOwnerType('chambers')}
                  disabled={saveMut.isPending}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-left text-xs transition ${
                    ownerType === 'chambers'
                      ? 'border-brand-600 bg-brand-50 text-brand-800 dark:text-brand-200'
                      : 'border-border bg-card hover:bg-muted'
                  }`}
                >
                  <Buildings size={13} weight="duotone" />
                  <span className="font-semibold">The chambers</span>
                </button>
                <button
                  type="button"
                  onClick={() => setOwnerType('barrister')}
                  disabled={saveMut.isPending}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-left text-xs transition ${
                    ownerType === 'barrister'
                      ? 'border-brand-600 bg-brand-50 text-brand-800 dark:text-brand-200'
                      : 'border-border bg-card hover:bg-muted'
                  }`}
                >
                  <UserIcon size={13} weight="duotone" />
                  <span className="font-semibold">A barrister</span>
                </button>
              </div>
              {ownerType === 'barrister' && (
                <div className="mt-2">
                  <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                    Which barrister
                  </label>
                  <select
                    value={barristerOwnerId ?? ''}
                    onChange={(e) => setBarristerOwnerId(e.target.value || null)}
                    disabled={saveMut.isPending || barristersQ.isLoading}
                    className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                  >
                    <option value="">— Pick a barrister —</option>
                    {barristersQ.data?.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.fullName}
                        {b.callYear ? ` (Call ${b.callYear})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <p className="mt-1.5 text-xs text-muted-foreground">
                Per-barrister accounts are personal — only the owning barrister and chambers admin can see / edit them.
              </p>
            </div>
          )}

          {ownerLocked && initial && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5 text-xs">
              {initial.ownerType === 'chambers' ? (
                <>
                  <Buildings size={12} weight="duotone" />
                  <span>Chambers-shared account · owner cannot be changed after creation</span>
                </>
              ) : (
                <>
                  <UserIcon size={12} weight="duotone" />
                  <span>
                    Personal account ·{' '}
                    {initial.barristerOwner
                      ? `${initial.barristerOwner.user.firstName} ${initial.barristerOwner.user.lastName}`
                      : 'unknown owner'}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Type — locked after create */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Account type
            </label>
            <div className="grid grid-cols-3 gap-1.5">
              {TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => !isEdit && setType(opt.value)}
                  disabled={isEdit || saveMut.isPending}
                  className={`flex flex-col items-start gap-0.5 rounded-md border px-2.5 py-2 text-left text-xs transition disabled:cursor-not-allowed ${
                    type === opt.value
                      ? 'border-brand-600 bg-brand-50 text-brand-800 dark:text-brand-200'
                      : 'border-border bg-card text-foreground hover:bg-muted'
                  }`}
                >
                  <span className="font-semibold">{opt.label}</span>
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {TYPE_OPTIONS.find((o) => o.value === type)?.help}
              {isEdit && ' • Type is locked after creation.'}
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Account label
            </label>
            <Input
              value={name}
              onChange={(e) => { clearField('name'); setName(e.target.value); }}
              placeholder="e.g. Lloyds — Chambers Operating"
              maxLength={100}
              disabled={saveMut.isPending}
              className={fieldErrors.name ? 'border-danger ring-2 ring-danger/20' : undefined}
            />
            <FieldError message={fieldErrors.name} />
          </div>

          {/* Bank + currency */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Bank name <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
              </label>
              <Input
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="e.g. Lloyds Bank"
                maxLength={100}
                disabled={saveMut.isPending}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Currency
              </label>
              <Input
                value={currency}
                onChange={(e) => { clearField('currency'); setCurrency(e.target.value.toUpperCase().slice(0, 3)); }}
                placeholder="GBP"
                maxLength={3}
                disabled={saveMut.isPending}
                className={`uppercase${fieldErrors.currency ? ' border-danger ring-2 ring-danger/20' : ''}`}
              />
              <FieldError message={fieldErrors.currency} />
            </div>
          </div>

          {/* Sort code + account number */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Sort code
              </label>
              <Input
                value={sortCode}
                onChange={(e) => {
                  // Auto-format to xx-xx-xx as the user types.
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                  let formatted = digits;
                  if (digits.length > 4) {
                    formatted = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
                  } else if (digits.length > 2) {
                    formatted = `${digits.slice(0, 2)}-${digits.slice(2)}`;
                  }
                  clearField('sortCode');
                  setSortCode(formatted);
                }}
                placeholder="12-34-56"
                disabled={saveMut.isPending}
                inputMode="numeric"
                className={fieldErrors.sortCode ? 'border-danger ring-2 ring-danger/20' : undefined}
              />
              <FieldError message={fieldErrors.sortCode} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Account number
              </label>
              <Input
                value={last4Source}
                onChange={(e) => { clearField('accountNumber'); setLast4Source(e.target.value); }}
                placeholder={
                  isEdit && last4
                    ? `Currently ending in ${last4} — leave blank to keep`
                    : '8-digit account number'
                }
                disabled={saveMut.isPending}
                inputMode="numeric"
                maxLength={20}
                className={fieldErrors.accountNumber ? 'border-danger ring-2 ring-danger/20' : undefined}
              />
              <FieldError message={fieldErrors.accountNumber} />
              {computedLast4 && !fieldErrors.accountNumber && (
                <div className="mt-1 text-xs text-muted-foreground">
                  Will store: <span className="font-semibold">•••• {computedLast4}</span>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Notes <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
            </label>
            <ExpandableTextarea
              label="Notes"
              value={description}
              onChange={setDescription}
              placeholder="Internal notes — not shown on receipts."
              rows={2}
              maxLength={500}
              disabled={saveMut.isPending}
            />
          </div>

          {/* Default toggle */}
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              disabled={saveMut.isPending}
              className="h-3.5 w-3.5 cursor-pointer accent-brand-600"
            />
            <span className="font-medium">Default {type} account</span>
            <span className="text-muted-foreground">
              shown on receipts &amp; invoices
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saveMut.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              const e = localErrors();
              setFieldErrors(e);
              if (Object.keys(e).length) {
                toast.error('Please fix the highlighted fields.');
                return;
              }
              saveMut.mutate();
            }}
            disabled={saveMut.isPending}
            className="gap-1.5"
          >
            {saveMut.isPending && <CircleNotch size={14} className="animate-spin" />}
            {isEdit ? 'Save changes' : 'Add account'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
